import { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

export interface ReviewSearchFilters {
  q: string;
  competition: string;
  team: string;
  dateFrom: string;
  dateTo: string;
  filter: 'all' | 'mine' | 'shared';
}

export const emptyReviewFilters: ReviewSearchFilters = {
  q: '',
  competition: '',
  team: '',
  dateFrom: '',
  dateTo: '',
  filter: 'all',
};

interface Props {
  filters: ReviewSearchFilters;
  onChange: (filters: ReviewSearchFilters) => void;
  onSearch: () => void;
  resultCount?: number;
}

export default function ReviewSessionSearchBar({ filters, onChange, onSearch, resultCount }: Props) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const set = (patch: Partial<ReviewSearchFilters>) => onChange({ ...filters, ...patch });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onSearch();
  };

  const activeAdvancedCount = [
    filters.competition,
    filters.team,
    filters.dateFrom,
    filters.dateTo,
    filters.filter !== 'all' ? filters.filter : '',
  ].filter(Boolean).length;

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="搜尋場次、隊伍、賽事…"
          value={filters.q}
          onChange={(e) => set({ q: e.target.value })}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: { sm: 480 } }}
        />
        <Button variant="outlined" startIcon={<SearchIcon />} onClick={onSearch} sx={{ flexShrink: 0 }}>
          搜尋
        </Button>
        <Button
          variant="text"
          color="inherit"
          startIcon={<TuneIcon />}
          endIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setAdvancedOpen((v) => !v)}
          sx={{ flexShrink: 0, color: activeAdvancedCount > 0 ? 'secondary.main' : 'text.secondary' }}
        >
          進階篩選{activeAdvancedCount > 0 ? ` (${activeAdvancedCount})` : ''}
        </Button>
      </Stack>

      <Collapse in={advancedOpen}>
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="賽事名稱"
                size="small"
                fullWidth
                value={filters.competition}
                onChange={(e) => set({ competition: e.target.value })}
                onKeyDown={handleKeyDown}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="隊伍"
                size="small"
                fullWidth
                value={filters.team}
                onChange={(e) => set({ team: e.target.value })}
                onKeyDown={handleKeyDown}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label="範圍"
                size="small"
                fullWidth
                value={filters.filter}
                onChange={(e) => set({ filter: e.target.value as ReviewSearchFilters['filter'] })}
              >
                <MenuItem value="all">全部場次</MenuItem>
                <MenuItem value="mine">我的場次</MenuItem>
                <MenuItem value="shared">分享給我</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="日期由"
                type="date"
                size="small"
                fullWidth
                value={filters.dateFrom}
                onChange={(e) => set({ dateFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="日期至"
                type="date"
                size="small"
                fullWidth
                value={filters.dateTo}
                onChange={(e) => set({ dateTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
            <Button size="small" onClick={() => onChange(emptyReviewFilters)}>清除</Button>
            <Button size="small" variant="contained" color="secondary" onClick={onSearch}>套用篩選</Button>
          </Stack>
        </Box>
      </Collapse>

      {resultCount !== undefined && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {resultCount} 個場次
        </Typography>
      )}
    </Box>
  );
}
